// Consumption-based billing configuration.
// Single in-code source of truth for the consumption-only model (pro deprecated,
// Business = pay-as-you-go, Enterprise = custom). Flip ENABLE_CONSUMPTION_BILLING
// to false to restore the legacy subscription flow.

import dayjs from "dayjs";

export const ENABLE_CONSUMPTION_BILLING = true;

// Derives the billing-period query params for a workspace (shared by the dashboard
// and the workspaces list so both read the exact same monthCosts window). Returns
// { billingPeriodStart, billingPeriodEnd } when the workspace carries usable
// billing period anchors, otherwise null (caller falls back to month/year).
// IMPORTANT: the STORED window is the source of truth. It used to be re-derived
// from the stored end's day-of-month with calendar-month arithmetic anchored to
// the browser clock — but the backend rewrites the anchors up to 6x/day (4h
// syncWorkspaceValidityWithStripe cron + invoice.paid, both using end = start
// + 30 days), so every rewrite moved the derived window edges and whole
// boundary days of requestLogs silently fell in/out of the UoI total. The stored
// window is used VERBATIM for the current cycle so the UI can never disagree
// with the window the backend actually bills; past cycles (dashboard month
// navigation) step whole 30-day cycles back from the stored start, mirroring
// the backend's fixed BILLING_PERIOD_DAYS = 30 convention.
export const getBillingPeriodParams = (workspace, selectedDate = dayjs()) => {
  const currentStart = dayjs(workspace?.billingPeriodStart);
  const currentEnd = dayjs(workspace?.billingPeriodEnd);
  if (!currentStart.isValid() || !currentEnd.isValid()) return null;
  // Corrupt stored pair — treat as unanchored so bad data falls back to the
  // calendar-month path instead of producing a garbage window.
  if (
    !currentEnd.isAfter(currentStart) ||
    currentEnd.diff(currentStart, "day") > 45
  )
    return null;

  // Current cycle (selectedDate within the stored window, incl. the default
  // "now"): stored window verbatim — the exact window the backend's
  // consumptionWatcher/debits treat as the current cycle.
  if (selectedDate.isBefore(currentStart, "day")) {
    // Past cycle (dashboard month navigation): step whole cycles back from the
    // stored start, keeping the backend's fixed 30-day cycle length (not
    // calendar-month arithmetic).
    const monthsBack = Math.max(
      1,
      Math.ceil(currentStart.diff(selectedDate, "month", true))
    );
    const periodStart = currentStart.subtract(monthsBack, "month");
    return {
      billingPeriodStart: periodStart.toISOString(),
      billingPeriodEnd: periodStart.add(30, "day").toISOString(),
    };
  }

  // Beyond the stored window (future month navigation): no cycle exists yet —
  // return null so the caller's month/year fallback shows that (empty) calendar
  // month, exactly like unanchored workspaces.
  if (selectedDate.isAfter(currentEnd, "day")) return null;

  return {
    billingPeriodStart: currentStart.toISOString(),
    billingPeriodEnd: currentEnd.toISOString(),
  };
};

// Total consumed UoI from a monthCosts payload — identical formula everywhere
// (dashboard headline, pricing screen, workspace cards).
export const getTotalUoiFromCosts = (data) =>
  (data?.apiUsage?.totalUoI || 0) + (data?.trainingUsage?.totalComputeUoI || 0);

// Currency: 1 Unit of Intelligence (UoI) = $0.01 USD. Same value used in UoIUsageModal.
export const USD_PER_UOI = 0.01;
export const UOI_PER_USD = 1 / USD_PER_UOI; // 100

// Minimum recharge amount (USD). Applies to top-ups and to the initial onboarding recharge.
export const MIN_RECHARGE_USD = 5;

// Balance (USD) at/under which the user is considered "low" on credits and nudged.
export const LOW_CREDIT_THRESHOLD_USD = 10;
// Balance (USD) at/under which credits are considered "exhausted" (hard stop surface).
export const EXHAUSTED_CREDIT_THRESHOLD_USD = 0;

// Preset recharge packs shown in RechargeSheet / pricing card.
export const RECHARGE_PACKS_USD = [20, 50, 100, 200];
// Slider max for the recharge sheet. Scoped to where people actually top up so
// the packs (20/50/100/200) visibly move the thumb. Larger amounts use the
// custom text input (MAX_RECHARGE_USD still enforced there).
export const RECHARGE_SLIDER_MAX_USD = 500;
// Absolute recharge cap (USD) — matches the monthly spend cap ceiling.
export const MAX_RECHARGE_USD = 10000;

// Default monthly spend cap (USD). null = no cap until the user sets one.
export const DEFAULT_MONTHLY_CAP_USD = null;
// Minimum monthly cap value (USD) the user can set.
export const MIN_MONTHLY_CAP_USD = 5;

// Per-workspace UoI budget is the user's chosen seat count (workspace.maxUsers)
// multiplied by the per-plan UoI-per-seat. This is the per-workspace spend cap,
// reused from the backend (billingLogicCore UOI_PER_SEAT / consumptionWatcher
// subscriptionUoIConfig). 1 UoI = $0.01.
export const UOI_PER_SEAT = {
  base: 500,
  pro: 1500,
  business: 5000,
  enterprise: 7500,
};
export const MIN_UOI_CAP = 5000; // one business seat
// Convert a UoI budget to seat count (maxUsers) and back.
export const uoiToSeats = (uoi, subscriptionType = "business") =>
  Math.max(
    1,
    Math.ceil(
      (Number(uoi) || 0) /
        (UOI_PER_SEAT[subscriptionType] || UOI_PER_SEAT.business)
    )
  );
export const seatsToUoI = (seats, subscriptionType = "business") =>
  Math.max(0, Number(seats) || 0) *
  (UOI_PER_SEAT[subscriptionType] || UOI_PER_SEAT.business);

// Upper ceiling of the per-workspace budget allocation slider: 100M UoI/month (= $1M).
export const MAX_WORKSPACE_UOI_BUDGET = 100000000;

// Default per-workspace budget allocation (in seats) applied on workspace
// creation. 5 business seats = 25,000 UoI — matches the default allocation the
// pricing cards advertise (business minimumSeats).
export const DEFAULT_NEW_WORKSPACE_SEATS = 5;

// Default auto-recharge configuration.
export const DEFAULT_AUTO_RECHARGE = {
  enabled: false,
  thresholdUsd: 5,
  amountUsd: 25,
};

// Recharge "state" for the top-bar indicator.
export const RECHARGE_STATES = {
  EXHAUSTED: "exhausted",
  LOW: "low",
  OK: "ok",
};

export const DEFAULT_CONSUMPTION_OBJECT = {
  totalUoI: 0,
  totalCostUSD: 0,
  requests: 0,
  requestsLast30Minutes: 0,
};

export const formatUsd = (value) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });
  return formatter.format(Number(value) || 0);
};

export const formatUoI = (value) => {
  return (Number(value) || 0).toLocaleString("en-AU");
};

// Compact UoI display (Australian convention): abbreviates thousands/millions so
// large budgets stay readable — 5000 -> "5K", 250000 -> "250K", 1250000 -> "1.25M".
export const formatUoICompact = (value) => {
  const n = Number(value) || 0;
  const compact = (divisor, suffix) =>
    `${Number((n / divisor).toFixed(2)).toLocaleString("en-AU", {
      maximumFractionDigits: 2,
    })}${suffix}`;
  if (Math.abs(n) >= 1000000) return compact(1000000, "M");
  if (Math.abs(n) >= 1000) return compact(1000, "K");
  return n.toLocaleString("en-AU");
};

// Inverse of formatUoICompact for text inputs: accepts "25K", "1.5m", "25,000"
// and "25000" (case-insensitive K/M suffix, en-AU comma grouping). NaN if unparseable.
export const parseUoICompact = (text) => {
  const raw = String(text ?? "")
    .trim()
    .replace(/,/g, "");
  const match = raw.match(/^(-?\d+(?:\.\d+)?)\s*([kKmM])?$/);
  if (!match) return NaN;
  const n = parseFloat(match[1]);
  if (Number.isNaN(n)) return NaN;
  const suffix = match[2]?.toLowerCase();
  if (suffix === "k") return n * 1000;
  if (suffix === "m") return n * 1000000;
  return n;
};

export const usdToUoI = (usd) => Math.round((Number(usd) || 0) / USD_PER_UOI);
export const uoiToUsd = (uoi) => (Number(uoI) || 0) * USD_PER_UOI;

// Tax ID types accepted by Stripe (customer tax_id). International coverage so
// businesses worldwide can provide a tax identifier that appears on invoices.
// { value: stripe type, label: shown to the user, placeholder: hint }
export const TAX_ID_TYPES = [
  {
    value: "au_abn",
    label: "Australia — ABN",
    placeholder: "11-digit ABN e.g. 51 824 753 556",
  },
  { value: "nz_gst", label: "New Zealand — GST", placeholder: "GST number" },
  {
    value: "eu_vat",
    label: "EU — VAT",
    placeholder: "EU VAT number e.g. DE123456789",
  },
  {
    value: "gb_vat",
    label: "United Kingdom — VAT",
    placeholder: "UK VAT number e.g. GB123456789",
  },
  {
    value: "us_ein",
    label: "United States — EIN",
    placeholder: "EIN e.g. 12-3456789",
  },
  { value: "ca_bn", label: "Canada — BN", placeholder: "Business Number" },
  { value: "sg_gst", label: "Singapore — GST", placeholder: "GST number" },
  { value: "sg_uen", label: "Singapore — UEN", placeholder: "UEN" },
  { value: "in_gst", label: "India — GST", placeholder: "GSTIN" },
  {
    value: "jp_cn",
    label: "Japan — Corporate Number",
    placeholder: "Corporate Number",
  },
  {
    value: "kr_brn",
    label: "South Korea — BRN",
    placeholder: "Business Registration Number",
  },
  { value: "za_vat", label: "South Africa — VAT", placeholder: "VAT number" },
  {
    value: "ae_trn",
    label: "UAE — TRN",
    placeholder: "Tax Registration Number",
  },
  { value: "sa_vat", label: "Saudi Arabia — VAT", placeholder: "VAT number" },
  {
    value: "hk_br",
    label: "Hong Kong — BR",
    placeholder: "Business Registration Number",
  },
  {
    value: "my_itn",
    label: "Malaysia — ITN",
    placeholder: "Income Tax Number",
  },
  { value: "th_vat", label: "Thailand — VAT", placeholder: "VAT number" },
  { value: "tw_vat", label: "Taiwan — VAT", placeholder: "VAT number" },
  { value: "ch_vat", label: "Switzerland — VAT", placeholder: "VAT number" },
  { value: "no_vat", label: "Norway — VAT", placeholder: "VAT number" },
];
